import { useState } from "react";

import type { ApiErrorBody } from "../../api/client";
import { capLabel, toolsOf, RESOLVER_GUARD_REMOVED, type McpCatalog } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useWrite } from "../../hooks/useWrite";
import { ForbiddenNotice, Gate } from "../authority/Gate";
import { Badge, Notice, Panel, Table, Td, Th } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { Button, Input, Select } from "../../f7";

interface QuotaResponse extends ApiErrorBody {
  applied?: Record<string, { daily_credits: number; mode: string }>;
  effective_within_seconds?: number;
  unprotected_resolvers?: string[];
}

export type Mode = "unlimited" | "gated" | "metered";

export function modeOf(n: number): Mode {
  if (n < 0) return "unlimited";
  if (n === 0) return "gated";
  return "metered";
}

export function valueFor(mode: Mode, metered: string): number {
  if (mode === "unlimited") return -1;
  if (mode === "gated") return 0;
  const n = Number(metered);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

/**
 * Edit a tier's per-tool caps.
 *
 * Every tool from the catalog is listed, not just those with a row — otherwise
 * ten rows read as "the rest are unmanaged" when the truth is "the rest
 * inherit the `*` default".
 *
 * The three-way control uses the API's own vocabulary (unlimited / gated /
 * metered) rather than raw -1 / 0 / N, because those numbers mislead: 0 is
 * more restrictive than any positive value, which no numeric input conveys.
 *
 * Submit is ALWAYS force=false first. There is no force checkbox — force is
 * reachable only from a 409 the operator has already read.
 */
export function CapEditor({
  config,
  tier,
  caps,
  catalog,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  caps: Record<string, number>;
  catalog: McpCatalog | null;
  onDone: () => void;
}) {
  const write = useWrite<QuotaResponse>(config);
  const [edits, setEdits] = useState<Record<string, number>>({});
  const [metered, setMetered] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<QuotaResponse | null>(null);

  const wildcard = caps["*"];
  // MCP removed `resolver_tools` along with the resolver-cap guard itself —
  // there is no server list of which tools are resolvers any more. `search_`
  // is the naming convention the removed guard was built on (see
  // admin/services/quotas.py's own comment on the MCP side), so it is the
  // best signal left for the warning below — a heuristic, not the authority
  // the old field was.
  const looksLikeResolver = (tool: string) => tool.startsWith("search_");
  const tools = toolsOf(catalog).length
    ? ["*", ...[...toolsOf(catalog)].sort()]
    : Object.keys(caps).sort();

  const dirty = Object.entries(edits);

  /**
   * A metered value the input shows but would not write.
   *
   * `valueFor` coerces anything non-positive or unparseable to 1, so typing
   * `0` (or clearing the box) displayed `0` and would have written `1`. Rather
   * than coerce silently or overwrite what someone is mid-way through typing,
   * the field is marked invalid and Review is withheld until it is a positive
   * integer. `0` is not a low cap — Gated is its own mode.
   */
  const badMetered = dirty
    .filter(([tool]) => modeOf(edits[tool]!) === "metered")
    .map(([tool]) => tool)
    .filter((tool) => {
      const raw = metered[tool];
      if (raw === undefined) return false;
      return !/^\d+$/.test(raw.trim()) || Number(raw) <= 0;
    });

  function setCap(tool: string, n: number) {
    const current = caps[tool];
    setEdits((e) => {
      if (n === current) {
        const { [tool]: _drop, ...rest } = e;
        return rest;
      }
      return { ...e, [tool]: n };
    });
  }

  function submit(reason: string, force = false) {
    const body: Record<string, unknown> = { caps: Object.fromEntries(dirty) };
    // EVERY cap write carries a reason, the forced one most of all. The force
    // path used to call `submit("", true)`, and `if (reason)` then dropped the
    // field — so the one write that overrode a safety guard was also the only
    // one landing on the audit record unexplained. See `onForce` below.
    if (reason) body.reason = reason;

    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
        query: force ? { force: true } : undefined,
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setEdits({});
        setOpen(false);
        onDone();
      });
  }

  return (
    <>
      {write.state.kind === "denied" && (
        <div className="mb-3">
          <ForbiddenNotice message={write.state.error.message} />
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mb-3">
          <Notice tone="red" title="Refused — nothing was written">
            {write.state.error.message}
          </Notice>
        </div>
      )}

      {result?.applied && (
        <div className="mb-3">
          <WriteResult title="Caps updated" onDismiss={() => setResult(null)}>
            <p>
              {Object.keys(result.applied).length} cap
              {Object.keys(result.applied).length === 1 ? "" : "s"} written.{" "}
              {effectFromSeconds(result.effective_within_seconds, "The change")} This page
              shows the new value immediately; merchants may still hit the old one until
              the cap cache expires.
            </p>
          </WriteResult>
        </div>
      )}

      <Table
        head={
          <tr>
            <Th>Tool</Th>
            <Th>Cap</Th>
            <Th>Source</Th>
          </tr>
        }
            >
        {tools.map((tool) => {
          const own = caps[tool];
          const edited = edits[tool];
          const effective = edited ?? own ?? wildcard;
          const mode = effective === undefined ? "unlimited" : modeOf(effective);
          const isDirty = tool in edits;

          return (
            <tr
              key={tool}
              className={`border-t border-slate-950/10 dark:border-white/10 ${isDirty ? "bg-brand-500/10" : ""}`}
            >
              <Td>
                <span className="ref">{tool}</span>
                {tool === "*" && (
                  <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">default for every tool</span>
                )}
                {looksLikeResolver(tool) && (
                  <span title={RESOLVER_GUARD_REMOVED}>
                    <Badge tone="amber">search resolver</Badge>
                  </span>
                )}
              </Td>
              <Td>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Select
                    value={mode}
                    onChange={(e) => setCap(tool, valueFor(e.target.value as Mode, metered[tool] ?? "50"))}
                  >
                    <option value="unlimited">Unlimited</option>
                    <option value="gated">Gated</option>
                    <option value="metered">Metered</option>
                  </Select>
                  {mode === "metered" && (
                    <div className="w-20">
                      <Input
                        value={metered[tool] ?? String(effective ?? 50)}
                        onChange={(e) => {
                          setMetered((m) => ({ ...m, [tool]: e.target.value }));
                          setCap(tool, valueFor("metered", e.target.value));
                        }}
                        inputMode="numeric"
                        invalid={badMetered.includes(tool)}
                        aria-label={`Daily credits for ${tool}`}
                      />
                    </div>
                  )}
                  {isDirty && effective !== undefined && (
                    <Badge tone={capLabel(effective).tone}>{capLabel(effective).text}</Badge>
                  )}
                </div>
              </Td>
              <Td>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {own !== undefined ? "own row" : "inherited from *"}
                </span>
              </Td>
            </tr>
          );
        })}
      </Table>

      {dirty.length > 0 && (
        <div className="sticky bottom-4 mt-4">
          <Panel className="flex flex-wrap items-center justify-between gap-3 p-4">
            <p className="text-sm">
              <span className="font-medium">{dirty.length}</span> cap
              {dirty.length === 1 ? "" : "s"} changed, not saved
              {badMetered.length > 0 && (
                <span className="ml-2 text-red-600 dark:text-red-400">
                  · {badMetered.length} need{badMetered.length === 1 ? "s" : ""} a daily
                  credit above zero — Gated is its own mode
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <Button outline onClick={() => setEdits({})}>
                Discard
              </Button>
              <Gate need="quota:write">
                <Button onClick={() => setOpen(true)} disabled={badMetered.length > 0}>
                  Review {dirty.length} change{dirty.length === 1 ? "" : "s"}
                </Button>
              </Gate>
            </div>
          </Panel>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={`Change ${dirty.length} cap${dirty.length === 1 ? "" : "s"} on ${tier}`}
        confirmLabel="Apply caps"
        requireReason
        reasonHint="e.g. tightening the free tier"
        /* THE THIRD type-to-confirm case, which ConfirmDialog's own docstring
           names and nothing implemented: setting the tier's `*` row to Gated
           refuses every tool on the tier that has no explicit row. That is a
           wider blast radius than deleting the `*` row, which already asks for
           this, and it was going through on a reason box alone. */
        typeToConfirm={edits["*"] === 0 ? tier : undefined}
        busy={write.state.kind === "submitting"}
        onCancel={() => setOpen(false)}
        onConfirm={(reason) => submit(reason, false)}
        effect="Cap changes reach merchants within ~5 minutes — the cap map is cached with a 5-minute TTL and nothing invalidates it across tasks. This page will show the new value immediately."
      >
        <p>
          This applies to <strong>every merchant</strong> on tier{" "}
          <span className="ref">{tier}</span>.
        </p>
        {dirty.some(([tool]) => looksLikeResolver(tool)) && (
          <Notice tone="amber" title="Capping a search tool">
            {RESOLVER_GUARD_REMOVED}
          </Notice>
        )}
        <ul className="mt-2 space-y-1 text-xs">
          {dirty.map(([tool, n]) => (
            <li key={tool}>
              <span className="ref">{tool}</span>{" "}
              <Badge tone={capLabel(caps[tool] ?? wildcard ?? -1).tone}>
                {capLabel(caps[tool] ?? wildcard ?? -1).text}
              </Badge>{" "}
              → <Badge tone={capLabel(n).tone}>{capLabel(n).text}</Badge>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs">
          The batch is all-or-nothing: nothing is written unless every change is accepted.
        </p>
      </ConfirmDialog>
    </>
  );
}
